import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { ChatMessage } from '../../hooks/useChat';

interface Tabs {
  options: string[];
  selected: string;
  onChange: (type: string) => void;
  labels?: Record<string, string>;
}

interface Props {
  title?: string;
  messages: ChatMessage[];
  isLoading: boolean;
  isInitialized: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onSend: (text: string) => void | Promise<void>;
  onClear: () => void;
  tabs?: Tabs;
}

const ChatPanel = ({
  title = 'Asistente Propi',
  messages,
  isLoading,
  isInitialized,
  isOpen,
  onToggle,
  onSend,
  onClear,
  tabs,
}: Props) => {
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!inputText.trim() || isLoading) return;
    const text = inputText;
    setInputText('');
    await onSend(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1050 }}>
      {isOpen && (
        <div
          style={{
            width: 408,
            height: 552,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
            overflow: 'hidden',
            background: '#fff',
            marginBottom: 12,
          }}
        >
          <div
            style={{
              background: 'linear-gradient(87deg, #172b4d 0%, #1a174d 100%)',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div className="d-flex align-items-center">
              <i className="ni ni-chat-round text-white mr-2" />
              <span
                className="text-white font-weight-bold"
                style={{ fontSize: 14 }}
              >
                {title}
              </span>
            </div>
            <div className="d-flex align-items-center">
              <button
                onClick={onClear}
                title="Nueva conversación"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0 8px',
                }}
              >
                <i
                  className="ni ni-refresh-02 text-white"
                  style={{ fontSize: 12 }}
                />
              </button>
              <button
                onClick={onToggle}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <i className="ni ni-fat-remove text-white" />
              </button>
            </div>
          </div>

          {tabs && tabs.options.length > 1 && (
            <div
              style={{
                background: '#fff',
                borderBottom: '1px solid #e9ecef',
                padding: '8px 16px',
                display: 'flex',
                gap: 8,
              }}
            >
              {tabs.options.map((type) => (
                <button
                  key={type}
                  onClick={() => tabs.onChange(type)}
                  style={{
                    padding: '4px 14px',
                    borderRadius: 20,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: tabs.selected === type ? 600 : 400,
                    background: tabs.selected === type ? '#5e72e4' : '#e9ecef',
                    color: tabs.selected === type ? '#fff' : '#525f7f',
                    transition: 'background 0.15s',
                  }}
                >
                  {tabs.labels?.[type] ?? type}
                </button>
              ))}
            </div>
          )}

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '12px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              background: '#f6f9fc',
            }}
          >
            {messages.length === 0 && !isLoading && (
              <div
                className="d-flex flex-column align-items-center justify-content-center h-100 text-muted"
                style={{ fontSize: 13 }}
              >
                <i
                  className="ni ni-chat-round mb-2"
                  style={{ fontSize: 32, opacity: 0.3 }}
                />
                ¿En qué puedo ayudarte?
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  justifyContent:
                    msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '8px 12px',
                    borderRadius:
                      msg.role === 'user'
                        ? '12px 12px 2px 12px'
                        : '12px 12px 12px 2px',
                    background: msg.role === 'user' ? '#5e72e4' : '#fff',
                    color: msg.role === 'user' ? '#fff' : '#32325d',
                    fontSize: 13,
                    lineHeight: 1.5,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: '12px 12px 12px 2px',
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  <Spinner size="sm" color="primary" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div
            style={{
              padding: '10px 12px',
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              gap: 8,
              background: '#fff',
              alignItems: 'center',
            }}
          >
            <Input
              type="text"
              placeholder={
                isInitialized ? 'Escribe un mensaje...' : 'Iniciando...'
              }
              value={inputText}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setInputText(e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={isLoading || !isInitialized}
              style={{ fontSize: 13, borderRadius: 20 }}
            />
            <Button
              color="primary"
              size="sm"
              onClick={handleSend}
              disabled={isLoading || !isInitialized || !inputText.trim()}
              style={{
                borderRadius: '50%',
                width: 36,
                height: 36,
                padding: 0,
                flexShrink: 0,
              }}
            >
              <i className="ni ni-send" style={{ fontSize: 14 }} />
            </Button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onToggle}
          style={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            background: 'linear-gradient(87deg, #5e72e4 0%, #825ee4 100%)',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(94,114,228,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i
            className={isOpen ? 'ni ni-fat-remove' : 'ni ni-chat-round'}
            style={{ color: '#fff', fontSize: 22 }}
          />
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
